<template>
	<v-dialog
        class="report-dialog"
	  	maxWidth="1500px"
		height="720"
		:modelValue="props.modelValue && open"
		@update:modelValue="emit('update:modelValue', $event)"
	>
		<v-card>
			<v-card-title>
                <div
                    class="deepi-row align-center gap-8"
                >
                    <v-icon
                        icon="icon-bug"
                        color="#3FB8D7"
                    />
                    <p
                        class="text-body-xl text-bold text-ellipsis"
                    >
                        {{ tr('report-dialog.title') }}
                    </p>
                    <div
                        class="deepi-row justify-end gap-8"
                    >
                        <v-btn
                            v-if="body === 'list'"
                            icon="icon-cloud"
                            color="#3FB8D7"
                            size="small"
                            :loading="loading"
                            @click="upsertReports"
                        />
                        <v-btn
                            v-if="body === 'list'"
                            icon="icon-plus"
                            color="#3FB8D7"
                            size="small"
                            :loading="loading"
                            @click="takeScreenshot"
                        />
                        <v-btn
                            v-if="body === 'form' && reportFormRef"
                            icon="icon-checkbox"
                            color="#3FB8D7"
                            size="small"
                            :loading="loading"
                            @click="saveReport"
                        />
                        <v-btn
                            v-if="body === 'details'"
                            icon="icon-arrow-left"
                            color="#3FB8D7"
                            size="small"
                            :loading="loading"
                            @click="showList"
                        />
                    </div>
                </div>
			</v-card-title>
            <v-card-text>
                <ReportsList
                    v-if="body === 'list'"
                    @click:row="seeReport"
                />
                <ReportForm
                    v-if="body === 'form' && screenshot"
                    ref="reportFormRef"
                    :screenshot="screenshot"
                />
                <ReportDetails
                    v-if="body === 'details' && report"
                    :report="report"
                />
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { useFetchClient } from '@deepidoo/playdoo/services/composables';

import { useTranslations } from '../../composables';
import { type Report } from '../../models';

import ReportDetails from '../reportDetails/ReportDetails.vue';
import ReportForm from '../reportForm/ReportForm.vue';
import ReportsList from '../reportsList/ReportsList.vue';

export interface ReportDialogProps {
    modelValue: boolean;
};

const props = defineProps<ReportDialogProps>();

const emit = defineEmits<{
    'update:modelValue': [modelValue: boolean];
}>();

const { post } = useFetchClient();
const { tr } = useTranslations();

const reportFormRef = ref<InstanceType<typeof ReportForm>>();

const open = ref(true);
const loading = ref(false);
const body = ref<'list' | 'form' | 'details'>('list');
const screenshot = ref<File | null>(null);
const report = ref<Report | null>(null);

const takeScreenshot = async (): Promise<void> => {
    loading.value = true;
    open.value = false;
	const stream = await navigator.mediaDevices.getDisplayMedia({
		video: true,
	  	audio: false,
	  	preferCurrentTab: true
	} as MediaStreamConstraints);

	const video = document.createElement('video');
	video.srcObject = stream;
	await video.play();

	await new Promise<void>((resolve) => {
		const check = (): void => {
			if (video.videoWidth > 0 && video.videoHeight > 0) {
				resolve();
			}
			else {
				requestAnimationFrame(check);
			}
	  	};
	  	check();
	});

	const canvas = document.createElement('canvas');
	canvas.width = video.videoWidth;
	canvas.height = video.videoHeight;
	const ctx = canvas.getContext('2d');

	if (!ctx) {
		stream.getTracks().forEach(track => track.stop());
		loading.value = false;
		return;
	}

	ctx.drawImage(video, 0, 0);
	stream.getTracks().forEach(track => track.stop());
	canvas.toBlob((blob) => {
		if (!blob) {
			loading.value = false;
			return;
		}
		screenshot.value = new File([blob], 'screenshot.png', { type: 'image/png' });
	}, 'image/png');
    loading.value = false;
    body.value = 'form';
    open.value = true;
};

const saveReport = async (): Promise<void> => {
    if (!reportFormRef.value) {
        return;
    }
    loading.value = true;
    const newReport = await reportFormRef.value.validate();
    if (!newReport) {
        loading.value = false;
        return;
    }
    screenshot.value = null;
    report.value = newReport;
    loading.value = false;
    body.value = 'details';
};

const seeReport = (newReport: Report): void => {
    report.value = newReport;
    body.value = 'details';
};

const showList = (): void => {
    body.value = 'list';
    report.value = null;
};

const upsertReports = async (): Promise<void> => {
    loading.value = true;
    await post(
        `${import.meta.env.VITE_REPORT_URL}graphql`,
        {
            query: `
                mutation UpsertReports {
                    upsertReports
                }
            `
        }
    );
    loading.value = false;
};
</script>

<style scoped lang="scss">
@use 'ReportDialog.scss';
</style>